import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { CurrencyDollarIcon } from '@heroicons/react/24/outline'

// Components
import { CheckBox } from '../CheckBox'
import { Modal } from '../Modal'

// Service
import { getTransactions } from '../../services/purchases.service'

// Utils
import { formatCurrency } from '../PurchaseReceipts/helpers'

/**
 * The refundable licenses and devices on this exhibitor, grouped by the order each was
 * bought on.
 *
 * The grouping isn't presentational: a gateway credit references exactly one original
 * transaction, so a selection spanning two orders is two credits, and an order that
 * hasn't settled can only be reversed in full.
 * @param {array} licenses
 * @param {array} rentalDevices
 * @returns array of `{ transactionId, items }`
 */
const groupByOrder = (licenses, rentalDevices) => {
  const items = [
    ...licenses
      .filter((license) => !license.isRefunded && !license.isFreeLicense && !!license.transaction)
      .map((license) => ({
        key: `license:${license.id}`,
        id: license.id,
        kind: 'license',
        transactionId: license.transaction,
        amount: parseFloat(license.purchasePrice) || 0,
        label: `License ${license.licenseId || ''}`.trim(),
        detail: license.assignee
          ? `${license.assignee.user.firstName} ${license.assignee.user.lastName}`
          : 'Unassigned',
      })),
    ...rentalDevices
      .filter((device) => !device.isRefunded)
      .map((device) => ({
        key: `device:${device.id}`,
        id: device.id,
        kind: 'device',
        transactionId: device.transaction,
        amount: parseFloat(device.purchasePrice) || 0,
        label: 'Rental Device',
        detail: device.isCancelled ? 'Cancelled' : 'Active',
      })),
  ]

  const groups = new Map()

  items.forEach((item) => {
    if (!groups.has(item.transactionId)) groups.set(item.transactionId, [])
    groups.get(item.transactionId).push(item)
  })

  return [...groups.entries()].map(([transactionId, groupItems]) => ({
    transactionId,
    items: groupItems,
  }))
}

/**
 *
 * RefundModal
 *
 * - Refunds any combination of an exhibitor's licenses and rented devices in one action
 * - Grouped by order, because that is how the money goes back: one credit per order,
 *   and an order that hasn't settled yet can only be reversed in full
 * - Carries EE's own note on why, which the exhibitor never sees
 */
const RefundModal = ({
  event,
  eventExhibitorId,
  initialSelection,
  licenses,
  loading,
  onSubmit,
  rentalDevices,
  setShowModal,
  showModal,
}) => {
  const [selectedKeys, setSelectedKeys] = useState(initialSelection)
  const [internalNote, setInternalNote] = useState('')
  const [transactionsById, setTransactionsById] = useState({})

  const groups = useMemo(() => groupByOrder(licenses, rentalDevices), [licenses, rentalDevices])

  // The order's settlement state decides whether part of it can come back, and its short
  // id is what EE and the exhibitor call it. Fetched here rather than threaded down: the
  // page that opens this doesn't otherwise need transactions.
  useEffect(() => {
    const loadTransactions = async () => {
      const response = await getTransactions(
        `/events/${event.id}/exhibitors/${eventExhibitorId}/transactions/?limit=100`,
      )

      if (response) {
        setTransactionsById(
          Object.fromEntries(response.results.map((transaction) => [transaction.id, transaction])),
        )
      }
    }

    loadTransactions()
  }, [event.id, eventExhibitorId])

  const toggle = (key) =>
    setSelectedKeys((selected) =>
      selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key],
    )

  const selectedItems = groups.flatMap((group) =>
    group.items.filter((item) => selectedKeys.includes(item.key)),
  )
  const total = selectedItems.reduce((sum, item) => sum + item.amount, 0)

  /**
   * Why this selection can't be refunded, or null when it can.
   *
   * Before an order settles the gateway will only reverse it in full, so a part of one
   * is refused — worth saying here rather than letting the action bounce.
   */
  const blockedReason = () => {
    const unsettledPartial = groups.find((group) => {
      const transaction = transactionsById[group.transactionId]
      if (!transaction || transaction.isSettled) return false

      const chosen = group.items.filter((item) => selectedKeys.includes(item.key))
      return chosen.length > 0 && chosen.length < group.items.length
    })

    if (!unsettledPartial) return null

    const transaction = transactionsById[unsettledPartial.transactionId]
    return `Order ${transaction.shortId} hasn't settled yet, so it can only be refunded in full. Select everything left on it, or wait for it to settle — usually within a day.`
  }

  const blocked = blockedReason()

  return (
    <Modal
      actions={[
        { type: 'cancel', label: 'Cancel', onClick: () => setShowModal(false) },
        {
          type: 'submit',
          label: selectedItems.length ? `Refund ${formatCurrency(total)}` : 'Refund',
          disabled: selectedItems.length === 0 || !!blocked,
          onClick: () =>
            onSubmit({
              licenseIds: selectedItems.filter((i) => i.kind === 'license').map((i) => i.id),
              rentalDeviceIds: selectedItems.filter((i) => i.kind === 'device').map((i) => i.id),
              internalNote,
            }),
        },
      ]}
      icon={<CurrencyDollarIcon className="h-5 stroke-white sm:h-6" />}
      content={
        <div className="mt-3 flex flex-col gap-4 sm:mt-5">
          <span className="text-sm text-gray-600">
            Select what to refund. The money goes back to the card each order was paid on, and the
            exhibitor is sent an updated invoice. Refunding doesn&apos;t end a license — cancel it
            separately if they should lose access.
          </span>

          <div className="flex max-h-72 flex-col gap-4 overflow-y-auto">
            {groups.map((group) => {
              const transaction = transactionsById[group.transactionId]

              return (
                <div key={group.transactionId} className="flex flex-col gap-2">
                  <div className="flex items-baseline justify-between border-b border-gray-200 pb-1">
                    <span className="text-xs font-bold uppercase tracking-wide text-gray-600">
                      {transaction ? `Order ${transaction.shortId}` : 'Order'}
                    </span>
                    {transaction && !transaction.isSettled && (
                      <span className="text-xs text-gray-500">Not yet settled</span>
                    )}
                  </div>

                  {group.items.map((item) => (
                    <div className="flex items-center justify-between gap-2" key={item.key}>
                      <CheckBox
                        label={`${item.label} — ${item.detail}`}
                        name={item.key}
                        onChange={() => toggle(item.key)}
                        value={selectedKeys.includes(item.key)}
                      />
                      <span className="whitespace-nowrap text-sm text-gray-600">
                        {formatCurrency(item.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              )
            })}

            {groups.length === 0 && (
              <span className="text-sm text-gray-500">
                Nothing on this exhibitor can be refunded — everything paid for has already had its
                money returned.
              </span>
            )}
          </div>

          <label className="flex flex-col gap-1" htmlFor="refund-internal-note">
            <span className="text-xs font-bold uppercase tracking-wide text-gray-600">
              Reason (internal only)
            </span>
            <textarea
              className="rounded-lg border border-gray-300 p-2 text-sm"
              id="refund-internal-note"
              maxLength={2000}
              onChange={(e) => setInternalNote(e.target.value)}
              placeholder="Why this refund was issued. Kept on the record for EE — the exhibitor never sees it."
              rows={2}
              value={internalNote}
            />
          </label>

          {blocked && <span className="text-sm text-error-dark">{blocked}</span>}
        </div>
      }
      loading={loading}
      open={showModal}
      setOpen={setShowModal}
      title="Refund"
    />
  )
}

RefundModal.defaultProps = {
  initialSelection: [],
  loading: false,
  rentalDevices: [],
}

RefundModal.propTypes = {
  event: PropTypes.object.isRequired,
  eventExhibitorId: PropTypes.string.isRequired,
  initialSelection: PropTypes.array,
  licenses: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  rentalDevices: PropTypes.array,
  setShowModal: PropTypes.func.isRequired,
  showModal: PropTypes.bool.isRequired,
}

export default RefundModal
